import { useState, useMemo, useCallback, useEffect, useContext } from 'react';
import { useSet } from 'react-use';
import { JSONbigParse, JSONbigStringify } from 'app/home/general-components/JsonBig';
import { ConnectionStatusValues } from 'app/home/home-components/connection-status/ConnectionStatus';
import { LocalizationContext } from 'app/localization/Context';
import { isInDemoMode, isInNonAdminDemoMode } from 'app/home/general-components/BrowserUtils';
import { getTimezone } from 'app/localization/Zone';

import useWebSocket from './WebSocket';
import useDeviceList from './Device'
import useScenarioList from './Scenario'
import useBindList from './Bind'
import useLogList from './Log'
import useRoom from './Room'
import useScenarioReports from './ScenarioReport'
import useAddRemoteController from './AddRemoteController';
import useAddDeviceController from './AddDeviceController';
import useGetPowerController from './GetPowerController';
import useSSIDList from './Wifi';
import useOTAProgress from './OTAProgress';
import useAudioController from './AudioFilesController';
import useUserManagement from './UserManagement';
import useLocalStorage from './LocalStorage';
import useInsightsAndCharts from './Insights';
import useAIChat from './AIChat';

import packageJson from '../../../package.json';
import { serverDomain } from './ServerDomain';
import log from 'app/home/general-components/Logging';


const serverName = packageJson.serverName;

function computeNegotiatedProtocol(version, isSolo) {
  return {
    version,
    isSolo: isSolo,
    hasDeviceSetting: isSolo || (version >= 9),
    hasLampX4InScenario: isSolo || (version >= 13),
    hasLampX4InBind: isSolo || (version >= 10),
    hasSednaInBind: !isSolo && (version >= 10),
    hasTuya: !isSolo && (version >= 16),
    hasRemoteInBindAndScenario: isSolo || (version >= 16),
    hasCurtain: isSolo || (version >= 17),
    hasScenarioSwitch: isSolo || (version >= 18),
    canOTADeviceFromClient: isSolo || (version >= 19),
    canOTAHubFromClient: isSolo || (version >= 19),
    handleConnectedSSIDEvent: isSolo || (version >= 20),
    hasUserManagement: isSolo || (version >= 20),
    // hasLanguageInUserManagement: true, // always should have :) // 21
    hasRelay4: isSolo || (version >= 21),
    hasRelay4InBind: isSolo || (version >= 21),
    hasRoom: !isSolo && (version >= 22),
    hasAudioController: !isSolo && (version >= 23),
    hasRemote: isSolo || (version >= 24),
    hasEvaporativeCooler: isSolo || (version >= 25),
    hasEvaporativeCoolerInBindAndScenario: isSolo || (version >= 26),
    hasMultiXBind: isSolo || (version >= 26),
    hasSyncSettings: false,
    canEditBind: isSolo || (version >= 28),
    canEditScenario: isSolo || (version >= 28),
    hasDontChangeInScenario: isSolo || (version >= 28), // for relay4, lampX4, lampX3, and dpst.
    hasScenarioInBind: isSolo || (version >= 29),
    hasWifiDoorSensor: isSolo || (version >= 30),
    hasEcoThermostat: isSolo || (version >= 30),
    hasWifiMotionSensor: isSolo || (version >= 31),
    canChangeHomeProfile: isSolo,
    hasMultipurposeSensor: isSolo || (version >= 32),
    canAddTitanLampX3: isSolo || (version >= 33),
    hasLampX3InBindAndScenario: isSolo || (version >= 33),
    hasLiteSwitchInAddDevice: isSolo || (version >= 33),
    supportSiriToRunScenario: isSolo,
    supportDayOfWeekInBind: !isSolo && (version >= 35),
    hasWC: isSolo || (version >= 36),
    hasDoorbell: !isSolo && (version >= 37), // see hasDoorbellInBindAndScenario
    hasDoorbellInBindAndScenario: !isSolo && (version >= 38),
    hasWCInBindAndScenario: isSolo || (version >= 38),
    hasElevator1: isSolo || (version >= 38),
    hasElevator1InBindAndScenario: isSolo || (version >= 38),
    hasCurtain1Lamp2: isSolo || (version >= 38),

    // Only for solo log v1 (which is nowhere now). No gw hasNewLogV1. We can remove support of this after the merge of NewLogV2 in solo production.
    hasNewLogV1: false, // TODO: remove this key

    hasNewLogV2: isSolo || (version >= 35),
    supportLogBeforeId: isSolo || (version >= 35),
    supportLogFilter: isSolo || (version >= 35),

    supportCallInBind: serverName !== "production-co",
    supportEmailInBind: serverName !== "production-co",
    supportSMSInBind: true,

    supportHubWebPushEvent: !isSolo && (version >= 1000) && (serverName === "staging"), // solo's webpush is on REST
    supportInsightsAndCharts: isSolo || (!isSolo && (version >= 1000) && (serverName === "staging")),
    supportsAIChat: (isSolo || (!isSolo && (version >= 1000))) && (serverName === "staging"),
    supportTimezoneInBind: isSolo && (serverName === "staging"),

    // see supportedProtocols if needed
  }
}
const supportedProtocols = [
  20, 21, 22, 23, 24, 25, 26,
  27, 28, 29, 30, 31, 32, 33,
  34, 35, 36, 37, 38, 39,
  1000]; // enables experimental features;

// function getPhoneNumber() {
//   if (serverName === "production")
//     return "+000464406525"
//   else
//     return "+000808799564"
// }

// function getAuthToken() {
//   return "ghsdfjsahdgfhjasdfgkasdf"
// }

const debugEvents = true;

function useGateway(homesManager, profile) {
  const l = useContext(LocalizationContext);

  const home = homesManager.activeHome;
  const isSolo = home.isSolo === true; // home.isSolo is either true or undefined
  const soloAuthToken = homesManager.soloAuthToken;
  const soloScenarioAuthToken = homesManager.soloScenarioAuthToken;
  const homesManagerOnGWProfile = homesManager.onGWProfile;
  const homesManagerOnPushToken = homesManager.onPushToken;
  const homesManagerOnSoloHomeProfile = homesManager.onSoloHomeProfile;
  const currentPushSubscriptionStr = homesManager.currentPushSubscriptionStr;

  const SOCKET_URL =
    home.readyForConnection ?
      'wss://' + serverDomain + '/ws/' + (isSolo ? 'solo' : 'gateway') + '/client'
      : undefined;

  const
    { socket, isConnected,
      setOnOpen, setOnMessage, setOnClose, setOnError } // eslint-disable-line no-unused-vars
      = useWebSocket(SOCKET_URL);

  const sendMessage = useCallback((msg) => {
    if (debugEvents)
      log("<--s--", msg);
    if (socket && socket.readyState === 1)
      socket.send(msg);
  }, [socket]);

  const sendEvent = useCallback((ev, msg) => {
    let data = JSONbigStringify({ ev: ev, msg: msg });
    sendMessage(data);
  }, [sendMessage]);

  useEffect(() => {
    profile.setSendEvent(sendEvent)
    return () => {
      profile.setSendEvent(undefined)
    }
  }, [sendEvent, profile]);

  // ------------- gateway data -------------

  const [updateStatus, setUpdateStatus] = useState('wait-to-handshake');
  const [eventsMarkedUpdated, { add: markEventGot, reset: resetEventsMark }] = useSet(new Set([]));

  const connectionStatus = useMemo( // connecting, updating, stable
    () => {
      const importantTrackedEvents = isSolo ?
        ["scenario_list", "bind_list", "device_list"] :
        ["scenario_list", "bind_list", "device_list", "gw_profile", "log_list"];

      if (!isConnected) {
        return ConnectionStatusValues.connecting;
      } else {
        if (updateStatus === 'update-started') {
          for (let i = 0; i < importantTrackedEvents.length; i++) {
            const ev = importantTrackedEvents[i];
            if (!eventsMarkedUpdated.has(ev)) {
              return ConnectionStatusValues.updating;
            }
          }
          return ConnectionStatusValues.stable;
        } else {
          return ConnectionStatusValues.connecting;
        }
      }
    }, [isConnected, updateStatus, eventsMarkedUpdated, isSolo]);

  const [dataFetchedOnce, setDataFetchedOnce] = useState(false);
  useEffect(() => {
    setDataFetchedOnce(b => b || (connectionStatus === ConnectionStatusValues.stable));
  }, [connectionStatus]);
  const [dataFetchedDelayed, setDataFetchedDelayed] = useState(false);
  useEffect(() => {
    setTimeout(() => {
      setDataFetchedDelayed(dataFetchedOnce);
    }, 500);
    // این رو اضافه کردم چون صفحه نبودن پروفایل یه لحظه میخواست بیاد جلو ... احتمالا چون داده‌های کاربر و این فلگ با هم تغییر میکردن
    // update: i added gateway.currentUser.dataNotFetched, so maybe this is not needed anymore
  }, [dataFetchedOnce]);

  // ------------ handle protocol versions ------------
  const [negotiatedProtocolVersion, setNegotiatedProtocolVersion] = useState(undefined);
  const negotiatedProtocol = useMemo(() => {
    const version = negotiatedProtocolVersion;
    return computeNegotiatedProtocol(version, isSolo);
  }, [negotiatedProtocolVersion, isSolo]);

  // TODO: we use "sendEvent" inside event parsers like 
  //       "device_list" or "bind_list" to make function for "remove" or ...
  //       which creates a fundamental "fault" when the disconnection 
  //       occures and "sendEvent" function changes.
  //       But it seems this "fault" never produce a "failure" 
  //       as we get "device_list", "bind_list", and other data
  //       on "connection open" and recreate these faulty objects again.
  //       By the way, we can use Ref for "sendEvent" to fix this issue, if needed.

  const [showDeleteAllDevicesButton, setShowDeleteAllDevicesButton] = useLocalStorage('showDeleteAllDevicesButton', false);

  const [currentUserId, setCurrentUserId] = useState(undefined);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  const { audioFilesData, audioFilesController, onAudioFilesEvent } = useAudioController(sendEvent);
  const { roomsData, roomsController, onRoomRelatedEvent } = useRoom(sendEvent);
  const [allUsers, currentUser, onUserRelatedEvent, addUser] = useUserManagement(currentUserId, sendEvent, negotiatedProtocol, profile);
  const [devices, setDeviceFolowers, onDeviceRelatedEvent] = useDeviceList(sendEvent, currentUser, roomsData.rooms);
  const [scenarios, onScenarioRelatedEvent, createNewScenario, editScenario] = useScenarioList(sendEvent);
  const [binds, onBindRelatedEvent, createNewBind, editBind] = useBindList(sendEvent);
  const { areLogsFetched, logs, onLogList, requestMoreLogs, refreshLogs, showSecondsInLog, setShowSecondsInLog, logFilter, setLogFilter } = useLogList(home, negotiatedProtocol, isAuthenticated, sendEvent);
  const [onAddDevice, addDeviceController, onSoloAddDevice] = useAddDeviceController(sendEvent, negotiatedProtocol, home.home_id);
  const [onAddRemote, addRemoteController] = useAddRemoteController(sendEvent);
  const [onGetPower, requestGetPower] = useGetPowerController(sendEvent);
  const [onOTAProgress, otaProgressController] = useOTAProgress(sendEvent);
  const [connectedSSIDInfo, onSSIDRelatedEvent] = useSSIDList();
  const scenarioReports = useScenarioReports(devices, binds);
  const { insights, allLampsChart, stateChartPerMac, onInsightsOrChartsEvent, setLastStateChartRefreshTime, requestStateChartForDevice } = useInsightsAndCharts(devices, sendEvent, negotiatedProtocol);
  const aiChat = useAIChat(sendEvent);

  const [gwUpdateInfo, setGWUpdateInfo] = useState({});
  const [gatewayCriticallyNeedsUpdate, setGatewayCriticallyNeedsUpdate] = useState(undefined);
  const [homeAvailabilityCriticalError, setHomeAvailabilityCriticalError] = useState({ message: undefined, time: undefined }); // message: removed, offline, shouldlogin

  // ------------- gateway message sender -------------

  const sendHandshake = useCallback(() => {
    if (isSolo) {
      if (home.home_id) {
        sendEvent("handshake", {
          "home_id": home.home_id,
          "supported_protocols": [1, 2, 3]
        });
      } else {
        console.log("error: home.home_id is", home.home_id);
      }
    } else {
      if (home.phoneNumber) {
        sendEvent("handshake", {
          "phone_number": home.phoneNumber,
          "supported_protocols": supportedProtocols
        });
      } else {
        console.log("error: home.phoneNumber is", home.phoneNumber);
      }
    };
  }, [sendEvent, home, isSolo]);

  const sendAuth = useCallback(() => {
    if (isSolo) {
      sendEvent("auth", { "client_token": soloAuthToken });
    } else {
      if (home.authToken) {
        sendEvent("auth", { "client_token": home.authToken });
      }
    }
  }, [sendEvent, home, soloAuthToken, isSolo]);

  const demoMode = isInDemoMode();
  const nonAdminDemoMode = isInNonAdminDemoMode();
  const sendRegister = useCallback(() => {
    if (home.authToken) {
      let msg = { // TODO: complete register data
        "os_version": "?",
        "mobile_brand": "?",
        "mobile_model": "?",
        "avatar": profile.avatar,
        "name": profile.name,
        "language": l.fullLanguage,
        "client_token": home.authToken,
      }
      if (demoMode) {
        msg["avatar"] = 2;
        msg["name"] = "مهمان";
        msg["language"] = "fa";
        if (nonAdminDemoMode) {
          msg["for_demo"] = true;
        }
      }
      sendEvent("register", msg);
    }
  }, [sendEvent, home, profile, l.fullLanguage, demoMode, nonAdminDemoMode]);

  const changeSoloHomeProfile = useCallback((content) => {
    sendEvent("change_solo_home_profile", content);
  }, [sendEvent]);

  // ------------- gateway events -------------

  const onHandshake = useCallback((message) => {
    /*
    403 // incorrect number
    460 // cl need update
    461 // gw need update
    404 // hub is onffile | OR | solo home is removed
    */
    if (message.msg.status === 200) {
      setNegotiatedProtocolVersion(message.msg.negotiated_protocol)
      setUpdateStatus('update-started');
      sendAuth();
      setGatewayCriticallyNeedsUpdate(false);
    } else if (message.msg.status === 461) {
      setGatewayCriticallyNeedsUpdate(true);
    } else if (message.msg.status === 404) {
      if (isSolo) {
        setHomeAvailabilityCriticalError({ message: "removed", time: (new Date()).getTime() });
      } else {
        setHomeAvailabilityCriticalError({ message: "offline", time: (new Date()).getTime() });
      }
    }
  }, [sendAuth, setNegotiatedProtocolVersion, setGatewayCriticallyNeedsUpdate, isSolo]);

  const onAuth = useCallback((message) => {
    /*
    404 // registering
    401 // waiting for accepting
    */
    if (message.msg.status === 200) {
      if (home.notifyAuthenticated)
        home.notifyAuthenticated();

      setIsAuthenticated(true);

      setHomeAvailabilityCriticalError({ message: undefined, time: undefined })

      setLastStateChartRefreshTime(0);

      sendEvent("scenario_list", {});
      sendEvent("bind_list", {});
      sendEvent("device_list", {});
      if (isSolo) {
        sendEvent("solo_home_profile", {});
      } else {
        sendEvent("gw_profile", {});
      }

      if (negotiatedProtocol.supportInsightsAndCharts) {
        sendEvent("insights", {});
        sendEvent("all_lamps_chart", { "timezone": getTimezone() });
      }

      // will be sent in the useLogList
      // sendEvent("log_list", {"language": l.fullLanguage});

      if (!negotiatedProtocol.isSolo) {
        if (negotiatedProtocol.handleConnectedSSIDEvent) {
          sendEvent("connected_ssid", {});
        } else {
          sendEvent("ssid_list", {});
        }
      }

      if (negotiatedProtocol.hasUserManagement) {
        // profile.sendLanguage();
        sendEvent("user_list", {});
      }
      // else {
      //   profile.sendProfileEvent();
      // }

      if (message.msg.user_id !== undefined) {
        setCurrentUserId(message.msg.user_id);
      } else if (message.msg.uuid !== undefined) {
        setCurrentUserId(message.msg.uuid);
      }

      if (negotiatedProtocol.hasRoom) {
        sendEvent("room_list", {});
      }

      if ((negotiatedProtocol.supportHubWebPushEvent) &&
        (currentPushSubscriptionStr !== undefined) &&
        (home.pushSubscriptionStr !== currentPushSubscriptionStr)) {
        sendEvent("push_token", {
          "token": currentPushSubscriptionStr,
          "device_os": "web-push",
        });
      }

    } else if (message.msg.status === 404) {
      sendRegister();
    } else if (isSolo) {
      /*
      401 | [SOLO] client does not access to the home, refresh home list
      403 | [SOLO] invalid token, login again (for all solo homes)
      */
      if (message.msg.status === 401) {
        setHomeAvailabilityCriticalError({ message: "removed", time: (new Date()).getTime() });
      } else if (message.msg.status === 403) {
        setHomeAvailabilityCriticalError({ message: "shouldlogin", time: (new Date()).getTime() });
      }
    }
  }, [sendRegister, sendEvent, negotiatedProtocol, setLastStateChartRefreshTime,
    home, isSolo, currentPushSubscriptionStr]);

  const onRegister = useCallback((message) => {
    if (home.notifyRegistered)
      home.notifyRegistered();
    sendAuth();
  }, [sendAuth, home]);

  const onGWProfile = useCallback((message) => {
    const currentVersionName = message.msg.current_version_name;
    const currentVersionId = message.msg.current_version_id;
    const isNewerVersionAvailable = message.msg.is_newer_version_available;
    const canUpdateFromClient = message.msg.can_update_from_client; // can be undefined
    const updateSeverityLevel = message.msg.update_severity_level;

    let info = {};
    info.isUpdateNotificationSupported = (isNewerVersionAvailable !== undefined);
    info.currentVersionName = currentVersionName;
    info.currentVersionId = currentVersionId;
    info.isNewerVersionAvailable = isNewerVersionAvailable;
    info.canUpdateFromClient = canUpdateFromClient === true; // default (undefiend) is false
    info.updateSeverityLevel = updateSeverityLevel;

    setGWUpdateInfo(info);
  }, []);

  // ------------- low level connection events -------------

  useEffect(() => {
    setOnClose((event) => {
      setIsAuthenticated(false);
      setUpdateStatus('wait-to-handshake');
      resetEventsMark();
    });
  }, [setOnClose, resetEventsMark]);

  useEffect(() => {
    setOnError((event) => {
      setUpdateStatus('wait-to-handshake');
      resetEventsMark();
    });
  }, [setOnError, resetEventsMark]);

  useEffect(() => {
    setOnOpen((event) => {
      sendHandshake();
    });
  }, [setOnOpen, sendHandshake]);

  useEffect(() => {
    setOnMessage((event) => {
      if (debugEvents)
        log('--r-->', event.data);
      let message = JSONbigParse(event.data);
      markEventGot(message.ev)
      switch (message.ev) {
        case 'handshake':
          onHandshake(message);
          break;
        case 'auth':
          onAuth(message);
          break;
        case 'register':
          onRegister(message);
          break;
        case 'change_power':
        case 'change_state':
        case 'device_list':
          onDeviceRelatedEvent(message);
          break;
        case 'scenario_list':
        case 'edit_scenario':
          onScenarioRelatedEvent(message);
          break;
        case 'bind_list':
        case 'edit_bind':
        case 'enable_bind':
        case 'remove_bind':
          if (negotiatedProtocol.hasMultiXBind) {
            onBindRelatedEvent(message);
          }
          break;
        case 'log_list':
          onLogList(message);
          break;
        case 'add_device':
          onAddDevice(message);
          break;
        case 'solo_add_device':
          onSoloAddDevice(message);
          break;
        case 'add_virtual_remote':
          onAddRemote(message);
          break;
        case 'ssid_list':
        case 'connected_ssid':
          onSSIDRelatedEvent(message);
          break;
        case 'hub_ota_progress':
        case 'ota_progress':
          onOTAProgress(message);
          break;
        case 'room_list':
        case 'change_room_order':
        case 'change_room':
        case 'new_room':
        case 'remove_room':
          onRoomRelatedEvent(message);
          break;
        case 'audio_files':
          onAudioFilesEvent(message);
          break;
        case 'push_token':
          if (homesManagerOnPushToken) {
            homesManagerOnPushToken(message);
          }
          break;
        case 'gw_profile':
          // message.msg.is_newer_version_available = true;
          // message.msg.update_severity_level = 3;
          if (homesManagerOnGWProfile) {
            homesManagerOnGWProfile(message);
          }
          onGWProfile(message);
          break;
        case 'solo_home_profile':
          if (homesManagerOnSoloHomeProfile) {
            homesManagerOnSoloHomeProfile(message);
          }
          break;
        case 'get_power':
          onGetPower(message);
          break;
        case 'change_user':
        case 'user_list':
          if (negotiatedProtocol.hasUserManagement) {
            onUserRelatedEvent(message);
          }
          break;
        case 'insights':
        case 'state_chart':
        case 'all_lamps_chart':
          onInsightsOrChartsEvent(message)
          break;
        default:
          log('unsupported event', message.ev);
          break;
      }
    });
  }, [setOnMessage, onHandshake, onAuth, onRegister, onGetPower, onOTAProgress,
    onDeviceRelatedEvent, onScenarioRelatedEvent, onBindRelatedEvent, onRoomRelatedEvent,
    onLogList, onAddDevice, onSoloAddDevice, onAddRemote, onUserRelatedEvent, onAudioFilesEvent,
    onSSIDRelatedEvent, homesManagerOnGWProfile, onGWProfile, homesManagerOnSoloHomeProfile,
    homesManagerOnPushToken, negotiatedProtocol, markEventGot,
    onInsightsOrChartsEvent]);

  const dispatchReconnection = useCallback(() => {
    if (socket)
      socket.close();
  }, [socket]);

  // window.____binds = binds;
  // window.____sendEvent = sendEvent;
  // window.____sendEvent("forced_change_state", {mac: "", ota_exists: true})

  // ------------- send data to upper layer -------------
  return {
    sendEvent,
    negotiatedProtocol,
    dispatchReconnection,
    connectionStatus, dataFetched: dataFetchedDelayed,
    changeSoloHomeProfile,
    devices, setDeviceFolowers, showDeleteAllDevicesButton, setShowDeleteAllDevicesButton,
    scenarios, createNewScenario, editScenario,
    binds, createNewBind, editBind,
    logs, areLogsFetched, requestMoreLogs, refreshLogs, showSecondsInLog, setShowSecondsInLog,
    logFilter, setLogFilter,
    insights, allLampsChart, stateChartPerMac, requestStateChartForDevice,
    addDeviceController, addRemoteController, requestGetPower, otaProgressController,
    connectedSSIDInfo,
    scenarioReports,
    gatewayCriticallyNeedsUpdate, gwUpdateInfo, homeAvailabilityCriticalError,
    allUsers, currentUser, currentUserId, addUser,
    roomsData, roomsController,
    audioFilesData, audioFilesController,
    soloScenarioAuthToken, isSolo,
    aiChat,
  };
};

export default useGateway;